import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Modal } from "@icg360/design-system";

import { EasyPayEnrollmentModal } from "components/easypay-enrollment-modal";
import { ExternalHeader } from "components/external-header";
import { PaperlessModal } from "components/paperless-modal";
import { AuthAppContext } from "components/root/auth-app-provider";
import { ENROLLMENT_STATUS, ORIGINS, PAYMENT_METHOD_CODE } from "consts";
import { useClaimsList } from "data/ss-data-hooks";
import {
  getCookie,
  getFullstorySession,
  getPaymentMethodDetails,
  identifyFullstoryUser,
  identifyUserMixpanel,
  isEasyPayEnrolledOrPending,
  isPaperlessDocumentEligible,
  setCookie,
  setMixpanelProfileProperties,
  setMixpanelSuperProperties,
  setSentryFeatFlags,
  setSentryLinks,
  setSentryTag,
  setSentryUser,
  trackEvent,
  urlSearchParamsObject,
  useFlags,
} from "utils";

import { trackClaimsInfo } from "./utils/track-claims-info";

type ModalName = "paperless" | "easypay" | "invalid-policy";

export const AuthApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useFlags();
  const [invalidPolicy, setInvalidPolicy] = useState("");
  const [paymentMethod, setPaymentMethod] = useState();
  const [curModal, setCurModal] = useState<ModalName | null>(null);

  const {
    policies,
    selectedPolicyId,
    userBilling,
    userDetails,
    userInfo,
    userPolicyProperties,
    isPolicyDataReady,
  } = useContext(AuthAppContext);
  const [initialPageDataLoaded, setInitialPageDataLoaded] = useState(false);
  useEffect(() => {
    if (isPolicyDataReady) {
      setInitialPageDataLoaded(true);
    }
  }, [isPolicyDataReady]);

  useEffect(() => {
    if (!userBilling) {
      return;
    }
    const curPaymentMethod = getPaymentMethodDetails(
      userBilling?.accounting?.paymentPlan?.planType?.toLowerCase()
    ).paymentMethod;
    setPaymentMethod(curPaymentMethod);
    setMixpanelSuperProperties({ paymentPlan: curPaymentMethod });
    setMixpanelProfileProperties({
      paymentPlan: curPaymentMethod,
    });
  }, [userBilling]);

  useEffect(() => {
    if (!userPolicyProperties) {
      return;
    }

    setMixpanelSuperProperties({
      carrier: userPolicyProperties?.carrierName,
      policyState: userPolicyProperties?.policyState,
    });
  }, [userPolicyProperties]);

  useEffect(() => {
    if (userDetails) {
      const { currentTerm, policyStatus, propertyAddress, insuredPreferences } =
        userDetails;

      setMixpanelSuperProperties({
        policyStatus,
        agencyLocationCode: currentTerm?.agencyLocationCode,
        propertyState: propertyAddress?.state,
        zipCode: propertyAddress?.zip,
      });

      setMixpanelProfileProperties({
        agencyLocationCode: currentTerm?.agencyLocationCode,
        paperlessDocumentsEnrollment:
          insuredPreferences?.paperlessDocumentEnrollmentStatus ||
          ENROLLMENT_STATUS.NOT_ENROLLED,
        paperlessDocumentsEmail:
          insuredPreferences?.opInsuredEmailAddressDocuments || "",
        paperlessBillingEnrollment:
          insuredPreferences?.paperlessBillingEnrollmentStatus ||
          ENROLLMENT_STATUS.NOT_ENROLLED,
        paperlessBillingEmail:
          insuredPreferences?.opInsuredEmailAddressBilling || "",
      });
    }
  }, [userDetails]);

  //********** START DATA FETCHING **********//
  const { isLoading: isClaimsLoading, data: claimsList } = useClaimsList();

  useEffect(() => {
    (async () => {
      if (!isClaimsLoading) {
        trackClaimsInfo(claimsList);
      }
    })();
  }, [claimsList, isClaimsLoading]);

  const parsedQS = urlSearchParamsObject();
  const isCommercial = userDetails?.isCommercial;
  const isEasyPayEnrolled = isEasyPayEnrolledOrPending(
    userBilling,
    userDetails
  );

  // Populate Qualtrics user data.
  useEffect(() => {
    if (policies) {
      window.SURVEY_USER_FIRST_NAME = userInfo?.firstName;
      window.SURVEY_USER_LAST_NAME = userInfo?.lastName;
      window.SURVEY_USER_EMAIL = userInfo?.email;
      window.SURVEY_USER_POLICY_NUMBERS = policies
        .map((p) => p?.policyId)
        .join(", ");
    }
  }, [policies, userInfo]);

  // Identify user to third-party systems
  useEffect(() => {
    if (!userInfo || !userDetails || !selectedPolicyId || !featureFlags) {
      return;
    }

    setSentryUser({
      userId: userInfo.userName ?? userInfo.userId ?? "",
      email: userInfo.email ?? "",
    });
    setSentryLinks({
      FullStory: getFullstorySession() ?? "",
    });
    setSentryFeatFlags(featureFlags);
    setSentryTag("policy", selectedPolicyId);

    identifyFullstoryUser({
      userId: userInfo.userName ?? userInfo.userId ?? "",
      email: userInfo.email ?? "",
      displayName: `${userInfo.firstName} ${userInfo.lastName}`,
    });
    identifyUserMixpanel({
      email: userInfo.email,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      userId: userInfo.userName ?? userInfo.userId,
    });

    // Set super properties
    setMixpanelSuperProperties({
      policyNumber: selectedPolicyId,
      isBookRoll: userDetails.isBookRoll,
    });
  }, [featureFlags, userDetails, selectedPolicyId, userInfo]);

  useEffect(() => {
    if (!isPolicyDataReady) {
      return;
    }

    const email = userInfo?.email?.toLowerCase() ?? "";

    setCookie("mss-username", btoa(email));
  }, [
    isPolicyDataReady,
    selectedPolicyId,
    userDetails,
    userInfo,
    userPolicyProperties,
  ]);

  useEffect(() => {
    if (
      !initialPageDataLoaded ||
      !paymentMethod ||
      location.pathname !== "/my/overview"
    ) {
      return;
    }
    initializeModals();
  }, [initialPageDataLoaded, paymentMethod, location.pathname]);

  const initializeModals = () => {
    if (parsedQS.origin === ORIGINS.DIGITAL_INVOICE) {
      // if the policyNumber were valid, it would have been removed from the qs (look in useAuthAppData)
      if (parsedQS.policyNumber) {
        trackEvent("Error Modal Displayed (policy not found)", {
          policyNumber: parsedQS.policyNumber,
          errorMessage: "Policy is not linked to the account",
        });
        setInvalidPolicy(parsedQS.policyNumber);
        setCurModal("invalid-policy");
        navigate("/my/overview");
      } else if (isEasyPayEnrolled) {
        trackEvent("Error Modal Displayed (easypay enrolled)", {
          policyNumber: selectedPolicyId,
          errorMessage: "Policy is already enrolled in EasyPay",
        });
        setCurModal("easypay");
      }
      navigate(location.pathname);
    } else {
      if (
        getCookie("mss-easypay-modal-dismissed") === undefined &&
        paymentMethod !== PAYMENT_METHOD_CODE.INVOICE &&
        !isEasyPayEnrolled
      ) {
        setCurModal("easypay");
      } else if (
        getCookie("mss-paperless-modal-dismissed") === undefined &&
        isPaperlessDocumentEligible(userDetails)
      ) {
        setCurModal("paperless");
      }
    }
  };

  return (
    <>
      <ExternalHeader returnUrlParsed />
      <Outlet />
      {initialPageDataLoaded && (
        <PaperlessModal
          show={curModal === "paperless" && !isCommercial}
          onClose={() => {
            setCurModal(null);
            setCookie("mss-paperless-modal-dismissed", true);
          }}
          documentsEmailAddress={
            userDetails?.insuredPreferences?.opInsuredEmailAddressDocuments ??
            userDetails?.insuredPreferences?.opInsuredEmailAddress ??
            ""
          }
          userDetails={userDetails}
          userBilling={userBilling}
          policyId={selectedPolicyId}
        />
      )}
      {paymentMethod !== PAYMENT_METHOD_CODE.INVOICE && (
        <EasyPayEnrollmentModal
          show={curModal === "easypay"}
          onClose={() => setCurModal(null)}
        />
      )}
      <Modal
        open={curModal === "invalid-policy"}
        heading="This policy is not in your account."
        onClose={() => setCurModal(null)}
        primaryAction="Add new policy"
        onPrimaryAction={() => {
          setCurModal(null);
          navigate("/my/settings/policies");
        }}
      >
        {`Policy # ${invalidPolicy} is not currently linked to your online account. To continue EasyPay enrollment, switch to another account or add new policy to your account.`}
      </Modal>
    </>
  );
};
